import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { StudioProvider, useStudio } from "@/context/StudioContext";
import Landing from "@/pages/Landing";
import Projects from "@/pages/Projects";
import Collection from "@/pages/Collection";
import ProductDetail from "@/pages/ProductDetail";
import Element from "@/pages/Element";
import { ELEMENTS } from "@/data/elements";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import Visualise from "@/pages/Visualise";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Brand from "@/pages/Brand";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";
import BookingDialog from "@/components/BookingDialog";
import SampleDialog from "@/components/SampleDialog";

// re-export for legacy imports
export { useStudio } from "@/context/StudioContext";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let in-page anchor scroll take over
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function GlobalOverlays() {
  const { bookingOpen, setBookingOpen, sampleOpen, setSampleOpen } = useStudio();
  return (
    <>
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
      <SampleDialog open={sampleOpen} onOpenChange={setSampleOpen} />
    </>
  );
}

function Shell() {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    // Coarse pointer = touch device → shorter duration + slightly lower lerp so
    // native touch inertia stays intact but wheel/trackpad on hybrid devices
    // still feels tuned. `smoothTouch` intentionally left OFF (Lenis best
    // practice): forcing smooth on touch breaks momentum scrolling on iOS.
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const lenis = new Lenis({
      duration: isTouch ? 0.9 : 1.15,
      smoothWheel: true,
      lerp: isTouch ? 0.12 : 0.09,
    });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [pathname]);

  return (
    <div className="App grain" style={{ background: "#121315" }}>
      <Toaster position="top-center" theme="dark" richColors duration={5000} style={{ zIndex: 999999 }} />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/archive" element={<Projects />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/collection" element={<Collection />} />
        <Route path="/collection/:productId" element={<ProductDetail />} />
        {ELEMENTS.map((e) => (
          <Route key={e.slug} path={`/${e.slug}`} element={<Element slug={e.slug} />} />
        ))}
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        <Route path="/journal" element={<Insights />} />
        <Route path="/visualize" element={<Visualise />} />
        <Route path="/visualise" element={<Visualise />} />
        <Route path="/samples" element={<Collection />} />
        <Route path="/products" element={<Collection />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/brand" element={<Brand />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <GlobalOverlays />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <StudioProvider>
        <Shell />
      </StudioProvider>
    </BrowserRouter>
  );
}

export default App;
